import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { 
  Users, GraduationCap, Clock, Briefcase, MapPin,
  CheckCircle, TrendingUp, Building, Award, TrendingDown,
  Calendar, Megaphone, ChevronUp, ChevronDown
} from 'lucide-react';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const CARD_COLORS = {
  blue: { bg: 'bg-blue-50/50', icon: 'bg-blue-100', text: 'text-blue-600' },
  green: { bg: 'bg-green-50/50', icon: 'bg-green-100', text: 'text-green-600' },
  purple: { bg: 'bg-purple-50/50', icon: 'bg-purple-100', text: 'text-purple-600' },
  orange: { bg: 'bg-orange-50/50', icon: 'bg-orange-100', text: 'text-orange-600' }
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7');

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/college/dashboard-stats?range=${timeRange}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch dashboard data');
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const StatCard = ({ title, value, trend, icon: Icon, subtext, color = "blue" }) => {
    const colors = CARD_COLORS[color];
    return (
      <div className={`${colors.bg} rounded-2xl p-6 border transition-all duration-300 hover:shadow-lg hover:scale-[1.02]`}>
        <div className="flex justify-between items-start">
          <div className={`${colors.icon} p-3 rounded-xl`}>
            <Icon className={`h-6 w-6 ${colors.text}`} />
          </div>
          {trend && (
            <div className={`flex items-center ${trend.isPositive ? 'text-green-600' : 'text-red-600'} bg-white/80 px-2 py-1 rounded-lg`}>
              <span className="text-sm font-medium">{trend.percentage}%</span>
              {trend.isPositive ? (
                <ChevronUp className="h-4 w-4 ml-1" />
              ) : (
                <ChevronDown className="h-4 w-4 ml-1" />
              )}
            </div>
          )}
        </div>
        <div className="mt-4">
          <h3 className={`text-3xl font-bold ${colors.text}`}>
            {value?.toLocaleString() || '0'}
          </h3>
          <p className="text-gray-600 text-sm mt-1 font-medium">{title}</p>
          {subtext && (
            <p className="text-sm text-gray-500 mt-2">{subtext}</p>
          )}
        </div>
      </div>
    );
  };

  const ChartCard = ({ title, subtitle, children }) => (
    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" />
          <p className="text-gray-600 font-medium">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md">
          <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-4">
            <TrendingDown className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { overview, departmentStats, yearWiseStats, employmentStats, 
          locationStats, profileCompletion, engagement } = dashboardData;

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive overview of your alumni network</p>
        </div>
        <div className="flex items-center gap-4">
          <select 
            className="px-4 py-2 border rounded-lg text-gray-600 bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 3 months</option>
            <option value="180">Last 6 months</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Alumni"
          value={overview.totalAlumni}
          icon={Users}
          trend={overview.registrationTrend}
          subtext="Overall registered alumni"
          color="blue"
        />
        <StatCard
          title="Verified Alumni"
          value={overview.verifiedAlumni}
          icon={CheckCircle}
          subtext={`${((overview.verifiedAlumni / overview.totalAlumni) * 100).toFixed(1)}% verified`}
          color="green"
        />
        <StatCard
          title="Profile Completion"
          value={`${profileCompletion.averageCompletion.toFixed(1)}%`}
          icon={Award}
          subtext={`${profileCompletion.completeProfiles} complete profiles`}
          color="purple"
        />
        <StatCard
          title="Employment Rate"
          value={`${((employmentStats.employed / employmentStats.total) * 100).toFixed(1)}%`}
          icon={Briefcase}
          subtext={`${employmentStats.employed} currently employed`}
          color="orange"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard 
          title="Department Distribution" 
          subtitle="Alumni distribution across departments"
        >
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={departmentStats}
                  dataKey="count"
                  nameKey="department"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  fill="#8884d8"
                  labelLine={false}
                  label={({ department, percentage }) => 
                    `${department} (${percentage}%)`
                  }
                >
                  {departmentStats.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      className="hover:opacity-80 transition-opacity"
                    />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`${value} alumni`, name]}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard 
          title="Graduation Year Trends" 
          subtitle="Year-wise alumni registration patterns"
        >
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={yearWiseStats}>
                <defs>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="verifiedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                />
                <Area 
                  type="monotone"
                  dataKey="total"
                  stroke="#4F46E5"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#totalGrad)"
                  name="Total Alumni"
                />
                <Area 
                  type="monotone"
                  dataKey="verified"
                  stroke="#10B981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#verifiedGrad)"
                  name="Verified Alumni"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard 
          title="Geographic Distribution" 
          subtitle="Alumni distribution by location"
        >
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={locationStats}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="location" type="category" width={90} />
                <Tooltip
                  formatter={(value) => [`${value} alumni`, 'Count']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#8B5CF6"
                  radius={[0, 4, 4, 0]}
                >
                  {locationStats.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      className="hover:opacity-80 transition-opacity"
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Recent Engagement" subtitle="Latest activities and interactions">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50/50 rounded-xl hover:bg-blue-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Recent Events</p>
                  <p className="text-sm text-gray-600">{engagement.recentEvents} events organized</p>
                </div>
              </div>
              <span className="text-2xl font-semibold text-blue-600">
                {engagement.recentEvents}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-green-50/50 rounded-xl hover:bg-green-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Job Postings</p>
                  <p className="text-sm text-gray-600">{engagement.recentJobs} new opportunities</p>
                </div>
              </div>
              <span className="text-2xl font-semibold text-green-600">
                {engagement.recentJobs}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-purple-50/50 rounded-xl hover:bg-purple-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Megaphone className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Active Campaigns</p>
                  <p className="text-sm text-gray-600">{engagement.recentCampaigns} ongoing campaigns</p>
                </div>
              </div>
              <span className="text-2xl font-semibold text-purple-600">
                {engagement.recentCampaigns}
              </span>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Profile Completion and Department Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard 
          title="Profile Completion Overview" 
          subtitle="Detailed breakdown of profile completion status"
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-blue-50/30 p-6 rounded-xl">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Completion Rate</p>
                <p className="text-3xl font-bold text-blue-600">
                  {profileCompletion.averageCompletion.toFixed(1)}%
                </p>
              </div>
              <div className="h-24 w-24">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Complete', value: profileCompletion.completeProfiles },
                        { name: 'Incomplete', value: profileCompletion.totalProfiles - profileCompletion.completeProfiles }
                      ]}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      innerRadius={28}
                      outerRadius={38}
                    >
                      <Cell fill="#4F46E5" />
                      <Cell fill="#E5E7EB" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <p className="text-sm text-gray-600">Complete Profiles</p>
                <p className="text-2xl font-semibold mt-1 text-gray-900">
                  {profileCompletion.completeProfiles.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <p className="text-sm text-gray-600">Total Profiles</p>
                <p className="text-2xl font-semibold mt-1 text-gray-900">
                  {profileCompletion.totalProfiles.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </ChartCard>

        <ChartCard 
          title="Department Analytics" 
          subtitle="Department-wise distribution and metrics"
        >
          <div className="space-y-4">
            {departmentStats.slice(0, 5).map((dept, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <p className="font-medium text-gray-900">{dept.department}</p>
                    <p className="text-sm text-gray-500">{dept.count.toLocaleString()} alumni</p>
                  </div>
                  <span className="text-sm font-medium px-2 py-1 rounded-lg bg-blue-100 text-blue-600">
                    {dept.percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-in-out"
                    style={{ 
                      width: `${dept.percentage}%`,
                      transition: 'width 1s ease-in-out'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default Dashboard;